import { NgForm } from '@angular/forms';
import { delay, first, of } from 'rxjs';
import { PersianDate } from '../core/persian-date';

export type EventBusType = {
  type: string;
  payload?: any;
};
export class EventId {
  Id?: number;
  Name?: string;
}
export enum LogLevel {
  Trace = 0,
  Debug = 1,
  Information = 2,
  Warning = 3,
  Error = 4,
  Critical = 5,
  None = 6
}
export class Guid {
  static Empty = '00000000-0000-0000-0000-000000000000';
  static newGuid() {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
      const r = Math.random() * 16 | 0, v = c == 'x' ? r : (r & 0x3 | 0x8);
      return v.toString(16);
    });
  }
  static isValid(val: string) {
    return /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(val);
  }
}
export class Patterns {
  static email = '([a-zA-Z0-9_\\-\\.\\+]+)@([a-zA-Z0-9_\\-\\.]+)(\\.[a-zA-Z]{2,})+';
  // static mobile = '^([0٠۰]|\\+[9٩۹][8٨۸]|[0٠۰][0٠۰][9٩۹][8٨۸]|[9٩۹][8٨۸])?([ ]|-|[()]){0,2}[9٩۹]([012345679]|[١٢٣٤٥٦٧٩٠]|[۱۲۳۴۵۶۷۹۰])([ ]|-|[()]){0,2}(?:(\\d|[١٢٣٤٥٦٧٨٩٠]|[۱۲۳۴۵۶۷۸۹۰])([ ]|-|[()]){0,2}){8}$';
  static mobile = '^([0٠۰]|\\+[9٩۹][8٨۸]|[0٠۰][0٠۰][9٩۹][8٨۸]|[9٩۹][8٨۸])?[9٩۹]([012345679]|[١٢٣٤٥٦٧٩٠]|[۱۲۳۴۵۶۷۹۰])(\\d|[١٢٣٤٥٦٧٨٩٠]|[۱۲۳۴۵۶۷۸۹۰]){8}$';
  static digit = '(\\d|[١٢٣٤٥٦٧٨٩٠]|[۱۲۳۴۵۶۷۸۹۰])';
}
declare const $: Function;
export function setTimeout$(cb: () => void, timer?: number) { of(true).pipe(delay(timer || 0), first()).subscribe(cb); }
export type breadcrumbItem = {
  title: string;
  link?: any[];
}
export class Utility {
  static extractText(html: string) {
    try { return $('<div/>').html(html || '').text(); }
    catch { return html; }
  }
  static equalDate(a: Date, b: Date) {
    return ((<any>a == 'Invalid Date') && (<any>b == 'Invalid Date')) || (!a && !b) || a?.getTime() == b?.getTime();
  }

  static hashCode(input: string, abs = false) {
    if (!input) return 0;
    var hash = 0, i, chr;
    if (input.length === 0) return hash;
    for (i = 0; i < input.length; i++) {
      chr = input.charCodeAt(i);
      hash = ((hash << 5) - hash) + chr;
      hash |= 0; // Convert to 32bit integer
    }
    return abs ? Math.abs(hash) : hash;
  }

  static remoteJobTypes = [
    'دورکاری',
    'کار در منزل',
    'کار در خانه',
    'غیر‌حضوری',
    'بیرون بر'
  ];

  static get iOS() {
    return [
      'iPad Simulator',
      'iPhone Simulator',
      'iPod Simulator',
      'iPad',
      'iPhone',
      'iPod'
    ].includes(navigator.platform)
      // iPad on iOS 13 detection
      || (navigator.userAgent.includes("Mac") && "ontouchend" in document)
  }

  static log: string[] = [];
  static commaSeparated(value: number) { return value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ","); }
  static normalizeObject(object: any) { return JSON.parse(JSON.stringify(object)); }
  static maskContactInfo(input: string, replace: string = "*******"): string {
    if (!input) return input;
    return Utility.maskEmail(Utility.maskPhoneNumbers(input, replace), replace);
  }
  static maskPhoneNumbers(input: string, replace: string): string {
    if (!input) return input;
    input = Utility.toEnDigits(input);
    return input.replace(/(\d)(?:[._\-\(\)\[\]\s]*\d){6,}/g, replace);
  }
  static maskEmail(input: string, replace: string): string {
    if (!input) return input;
    input = Utility.toEnDigits(input);
    return input.replace(/([a-zA-Z0-9_\-\.\+])[a-zA-Z0-9_\-\.\+]*@([a-zA-Z0-9_\-\.])[a-zA-Z0-9_\-\.]*\.([a-zA-Z]{2,})+/g, replace);
  }

  static sum(array: number[], prop?: string) {
    if (!array) return null;
    if (prop) return this.sum(array.map(_ => _?.[prop]));
    return array.reduce((a, b) => (a || 0) + (b || 0), 0);
  }

  private static p2e = (s: string) => !s || !s.replace ? s : s.replace(/[۰-۹]/g, d => '۰۱۲۳۴۵۶۷۸۹'.indexOf(d).toString());
  private static a2e = (s: string) => !s || !s.replace ? s : s.replace(/[٠-٩]/g, d => '٠١٢٣٤٥٦٧٨٩'.indexOf(d).toString());
  static toEnDigits(s: string) {
    if (!s) return s;
    return Utility.p2e(Utility.a2e(s));
  }

  static esccapeRegex(term: string) {
    return term?.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }

  static removeWords(input: string, words: string[]) {
    words.forEach(_ => input = input.replace(new RegExp(`( |^)${this.esccapeRegex(_)}( |$)`, 'g'), ' '));
    return input.trim();
  }
  static trimChar(input: string, ...args: string[]) {
    for (var c of args) {
      while (input.startsWith(c)) input = input.substring(1);
      while (input.endsWith(c)) input = input.substring(0, input.length - 1);
    }

    return input;
  }

  static finglishCharCode = {
    'a': 'ش',
    'b': 'ذ',
    'c': 'ز',
    'd': 'ی',
    'e': 'ث',
    'f': 'ب',
    'g': 'ل',
    'h': 'ا',
    'i': 'ه',
    'j': 'ت',
    'k': 'ن',
    'l': 'م',
    'm': 'ئ',
    'n': 'د',
    'o': 'خ',
    'p': 'ح',
    'q': 'ض',
    'r': 'ق',
    's': 'س',
    't': 'ف',
    'u': 'ع',
    'v': 'ر',
    'w': 'ص',
    'x': 'ط',
    'y': 'غ',
    'z': 'ظ',
    '[': 'ج',
    ']': 'چ',
    '\\': 'پ',
    ';': 'ک',
    '\'': 'گ',
    ',': 'و',
    'ش': 'a',
    'ذ': 'b',
    'ز': 'c',
    'ی': 'd',
    'ث': 'e',
    'ب': 'f',
    'ل': 'g',
    'ا': 'h',
    'ه': 'i',
    'ت': 'j',
    'ن': 'k',
    'م': 'l',
    'ئ': 'm',
    'د': 'n',
    'خ': 'o',
    'ح': 'p',
    'ض': 'q',
    'ق': 'r',
    'س': 's',
    'ف': 't',
    'ع': 'u',
    'ر': 'v',
    'ص': 'w',
    'ط': 'x',
    'غ': 'y',
    'ظ': 'z',
    'ج': '[',
    'چ': ']',
    'پ': '\\',
    'ک': ';',
    'گ': '\'',
    'و': ',',
  };

  static correctWrongKeyword(input: string) {
    if (!input)
      return input;
    input = input.toLowerCase();
    for (var i = 0; i < input.length; i++)
      input = this.setCharAt(input, i, this.finglishCharCode[input[i]]);
    return input;
  }

  static setCharAt(input: string, index: number, char: string) {
    if (index > input.length - 1) return input;
    return input.substring(0, index) + char + input.substring(index + 1);
  }

  static layoutScrollClassname = 'kp-layout-scroll';
  static hasProp(obj: any) {
    if (!obj)
      return false;
    for (var p in obj)
      if (obj.hasOwnProperty(p))
        return true;
    return false;
  }

  static getBreadcrumb(state: string, city: string, keyword: string, ...additional: breadcrumbItem[]): breadcrumbItem[] {
    if (!state && !keyword && !additional?.length) return null;

    var items: breadcrumbItem[] = [{
      title: 'استخدام',
      link: ['/job']
    }];

    if (state) {
      items.push({
        title: `استان ${state}`,
        link: ['/job', { l: state }]
      });

      if (city)
        items.push({
          title: city,
          link: ['/job', { l: `${state}،${city}` }]
        });
    }

    if (keyword)
      items.push({
        title: keyword,
        link: ['/job', Utility.removeNull({ l: !state ? undefined : Utility.locationSearch(state, city), q: keyword })]
      });

    items.push(...additional);
    return items;
  }

  static getLocation(location: string) {
    const split = (location || '').split('،').map(_ => _.trim());
    return { State: split.length > 0 ? split[0] : '', City: split.length > 1 ? split[1] : '' };
  }
  static locationSearch(state: string, city: string) {
    if (!state)
      return undefined;
    if (!city)
      return state;
    return [state, city].join('،');
  }
  static locationString(state: string, city: string = null, district: string = null, addGlobal?: boolean) {
    if (!state)
      return 'سراسر کشور';
    return (!city
      ? `استان ${state}`
      : `${city}` + (district ? ` - ${district}` : '')
    ) + (addGlobal ? ` و سراسر ایران` : '');
  }
  static regionString(state: string, city: string = null, district: string = null) {
    if (!state) return '';
    return (!city
      ? `استان ${state}`
      : `${state}، ${city}` + (district ? ` - ${district}` : '')
    );
  }
  static removeNull(obj: any) {
    for (var p in obj)
      if (obj.hasOwnProperty(p) && (obj[p] === null || obj[p] === undefined))
        delete obj[p];
    return obj;
  }
  static removeUndefined(obj: any) {
    for (var p in obj)
      if (obj.hasOwnProperty(p) && obj[p] === undefined)
        delete obj[p];
    return obj;
  }
  static removeEmpty(obj: any, removeUnderlined = false, removeEmptyObjects = false) {
    for (var p in obj)
      if (obj.hasOwnProperty(p) && (
        obj[p] === undefined ||
        !(obj[p]?.toString()) ||
        (removeUnderlined && p.startsWith('_')) ||
        (removeEmptyObjects && JSON.stringify(obj[p]) === '{}')
      )) delete obj[p];
    return obj;
  }
  /// <summary>
  /// wraps the given words in a string with the given tag (default is 'em')
  /// term could be space-separated words
  /// </summary>
  static highlightSearch(text: string, term: string, tag: string = 'em'): string {
    let res = text?.toString();
    if (term)
      term.split(' ').filter(_ => _).forEach(_ => {
        let r = new RegExp(this.esccapeRegex(_), 'gi');
        res = res?.replace(r, s => `<${tag}>${s}</${tag}>`);
      });
    return res;
  }
  static wordSubstring(input: string, len: number) {
    if (!input) return '';
    if (input.length < len) return input;
    const res = input.substring(0, len);
    return res.substring(0, res.lastIndexOf(' ')) + '...';
  }
  static concatUnique(arr1: any[], arr2: any[]): any {
    return this.getUnique([...(arr1 || []), ...(arr2 || [])]);
  }
  static getUnique(arr: any[]): any[] {
    if (arr.length > 0 && typeof arr[0] === 'object')
      return [...new Set(arr.map(_ => JSON.stringify(_)))].map(_ => JSON.parse(_));
    else
      return [...new Set(arr)];
  }
  //get most frequent value in array
  static getMode(arr: any[]): any {
    return arr.sort((a, b) =>
      arr.filter(v => v === a).length
      - arr.filter(v => v === b).length
    ).pop();
  }
  static removeDuplicates(arr: any[]) {
    return [...new Set(arr)];
  }
  static isInArray(array: any[], obj: any) {
    return array && array.indexOf(obj) >= 0;
  }
  static setInArray(array: any[], key: any, set: boolean) {
    if (array == null) array = [];
    const i = array.indexOf(key);
    if (i >= 0 && !set) { array.splice(i, 1); }
    if (i < 0 && set) { array.push(key); }
  }
  static toggleInArray(array: string[], key: string) {
    const set = !Utility.isInArray(array, key);
    Utility.setInArray(array, key, set);
  }
  static removeAt(array: any[], index: number) {
    if (array == null) return;
    array.splice(index, 1);
  }
  static arrayFromRange(size: number, startAt: number = 0): ReadonlyArray<number> {
    return [...Array(size).keys()].map(i => i + startAt);
  }
  static validate(form: NgForm) {
    if (form.invalid)
      form.control.markAllAsTouched();
    return !form.invalid;
  }

  static isDifferent(obj1: any, obj2: any): boolean {
    return JSON.stringify(obj1) !== JSON.stringify(obj2);
  }
  static isEmpty(obj: any) { return !obj || JSON.stringify(obj) === '{}' || JSON.stringify(obj) === '[]'; }
  static headerHeight = 60;
  static get sourceDomain(): string {
    return sessionStorage.getItem('util-source-domain');
  }
  static set sourceDomain(value: string) {
    sessionStorage.setItem('util-source-domain', value);
  }
  static getUtmQueryUrl(utm: any): string {
    return (utm ? (Object.getOwnPropertyNames(utm).map(_ => `${_}=${encodeURI(utm[_])}`).join('&')) : '')
  }
  static random(min: number, max: number) {
    return Math.floor(Math.random() * (max - min) + min);
  }
  static nonZeroAverage(numbers: number[]) {
    const list = numbers.filter(_ => !isNaN(_) && _ != 0);
    if (list.length == 0)
      return 0;
    var sum = list.reduce((a, b) => a + b);
    return (sum * 1.0) / list.length;
  }


  static get mobile() { return window.screen == undefined ? true : window.screen.width < 768; }
  static showHeader = true;
  static showFooter = true;
  static getQueryParam(location: Location, param: string): string {
    return Utility.getUrlParam(location.search.slice(1), param);
  }
  static getHashParam(location: Location, param: string): string {
    return Utility.getUrlParam(location.hash.slice(1), param);
  }
  static getUrlParam(str: string, param: string) {
    if (!str || str.indexOf(param + '=') == -1) {
      return null;
    }
    const val = str.split('&')
      .filter(_ => _.startsWith(param))[0].split('=')[1];
    return decodeURIComponent(val);
  }
  static getUrlOrigin(url: string) {
    if (!url) return null;
    try { return new URL(url).origin; }
    catch { return null; }
  }


  static clone(item: any): any {
    return item === undefined ? undefined : Array.isArray(item)
      ? item.map(_ => this.clone(_)) : JSON.parse(JSON.stringify(item));
  }
  static fileSize(bytes: number) {
    if (bytes < 1000) { return bytes + ' B'; }
    if ((bytes /= 1024) < 1000) { return Math.round(bytes) + ' kB'; }
    return Math.round(bytes / 1024 * 100) / 100 + ' MB';
  }

  static sortProperties(params: any) {
    let _params = {};
    let properties = [];
    for (let p in params)
      if (params[p])
        properties.push(p);
    properties.sort((a, b) => a > b ? 1 : -1).forEach(p => _params[p] = params[p]);
    return _params;
  }

  static isElementInViewport(el) {
    if (!el) return false;
    let rect = el.getBoundingClientRect(),
      windowHeight = (window.innerHeight || document.documentElement.clientHeight);

    return !(
      Math.floor(100 - (((rect.top >= 0 ? 0 : rect.top) / +-rect.height) * 100)) < 0 ||
      Math.floor(100 - ((rect.bottom - windowHeight) / rect.height) * 100) < 0
    )
  }

  static setClipboard(text: string) {
    if (navigator.clipboard) { navigator.clipboard.writeText(text); } else {
      let element = <HTMLInputElement>document.getElementById('clipboard-helper');
      if (!element) {
        element = document.createElement('input');
        element.style.cssText = 'display:none !important';
        element.readOnly = true;
        document.body.appendChild(element);
      }
      element.value = text;

      if (!navigator.userAgent.match(/ipad|ipod|iphone/i) && (<HTMLInputElement>element).select) {
        (<HTMLInputElement>element).select();
      } else {
        const editable = element.contentEditable;
        element.contentEditable = 'true';
        const range = document.createRange();
        range.selectNodeContents(element);
        const sel = window.getSelection();
        sel.removeAllRanges();
        sel.addRange(range);
        if ((<HTMLInputElement>element).setSelectionRange) {
          (<HTMLInputElement>element).setSelectionRange(0, 999999);
        }
        element.contentEditable = editable;
      }

      try { return document.execCommand('copy'); } catch (err) { return false; }
    }
    return true;
  }

  static async share(item: { title?: string, url?: string }): Promise<boolean> {
    return new Promise<boolean>(resolve => {
      if ((<any>navigator).share) {
        (<any>navigator).share(item)
          .then(() => resolve(true))
          .catch(() => {
            this.setClipboard(item.url || item.title);
            resolve(false);
          });
      } else {
        this.setClipboard(item.url || item.title);
        resolve(false);
      }
    });
  }

  static copyTableRows(tableSelector: string, onlySelected = true) {
    let table = $(tableSelector);
    let text = '';
    table.find('th').each((i, _) => { if (i) text += `${$(_).text()}\t`; });
    text += '\n';
    let rows = onlySelected ? table.find('tbody tr[data-selected=true]') : table.find('tbody tr');
    rows.each((i, tr) => {
      $(tr).find('td').each((j, td) => { if (j) text += `${$(td).text()}\t`; });
      text += '\n';
    });
    Utility.setClipboard(text);
  }

  static isObject(item) { return (item && typeof item === 'object' && !Array.isArray(item)); }
  static merge(target, ...sources) {
    if (!sources.length) return target;
    const source = sources.shift();

    if (Utility.isObject(target) && Utility.isObject(source)) {
      for (const key in source) {
        if (Utility.isObject(source[key])) {
          if (!target[key]) Object.assign(target, { [key]: {} });
          Utility.merge(target[key], source[key]);
        } else if (source[key] != undefined) {
          Object.assign(target, { [key]: source[key] });
        }
      }
    }

    return Utility.merge(target, ...sources);
  }

  static assign(source: any, item: any, clone = false) {
    if (!source) source = {};
    if (clone) source = Utility.clone(source);
    Object.getOwnPropertyNames(item).forEach(_ => {
      if (item[_] === 'null')
        source[_] = null;
      else
        switch (typeof (source[_])) {
          case 'boolean':
            source[_] = item[_] == true || item[_] === 'true';
            break;
          case 'number':
            source[_] = +item[_];
            break;
          default:
            source[_] = item[_];
            break;
        }
    });
    return this.removeUndefined(source);
  }

  static join(seperator: string, ...values: string[]) {
    return values.filter(_ => _).join(seperator);
  }

  static groupBy(data: any[], prop: string): { [key: string]: any[] } {
    return [...data].reduce(function (rv, x) {
      (rv[x[prop]] = rv[x[prop]] || []).push(x);
      return rv;
    }, {});
  }

  static flat(array: any[]) {
    if (!array?.length) return [];
    if ((<any>array).flat) { return (<any>array).flat(); }
    const result = [];
    array.forEach(_ => {
      if (_ instanceof Array) { result.push(...this.flat(_)); } else { result.push(_); }
    });
    return result;
  }

  static distinct(data: any[], prop?: string | number): any[] {
    if (!data?.length) return [];
    return Object.keys([...data].reduce(function (rv, x) {
      (rv[(prop ? x[prop] : x)] = rv[(prop ? x[prop] : x)] || []).push(0);
      return rv;
    }, {}));
  }

  static distinctBy(data: any[], prop?: string): any[] {
    if (!data?.length) return [];
    if (!prop) return this.distinct(data);
    var result = [];
    data.forEach(_ => {
      if (!result.find(x => x?.[prop] == _?.[prop])) result.push(_);
    });
    return result;
  }

  static equal(a: any | any[], b: any | any[], prop?: string): boolean {
    if ((a == null || a.length == 0) && (b == null || b.length == 0)) return true;
    if ((a == null || a.length == 0) || (b == null || b.length == 0) || a.length != b.length) return false;

    if (Array.isArray(a) || Array.isArray(b))
      return !a.some(aa => !b.some(bb => prop ? aa[prop] === bb[prop] : aa === bb));
    return prop ? JSON.stringify(a[prop]) === JSON.stringify(b[prop]) : JSON.stringify(a) === JSON.stringify(b);
  }

  static getPropertyList(obj, recursive = false, stack = '') {
    if (!obj) return [];
    let r1 = [];
    let r2 = [];
    for (var p in obj) {
      if (obj.hasOwnProperty(p)) {
        if (recursive && typeof obj[p] == "object") {
          r2 = [...(Utility.getPropertyList(obj[p], recursive, (stack ? stack + '.' : '') + p))];
        } else {
          r1.push((stack ? stack + '.' : '') + p);
        }
      }
    }
    return [...r1, ...r2];
  }
  static getProperty(object: any, propertyName: string) {
    var parts = propertyName.split("."), length = parts.length, property = object;
    for (var i = 0; i < length; i++)
      property = property[parts[i]];
    return property;
  }
  static projectObject0(obj: any, fields: string[], stringify = false): any {
    if (!obj) return null;
    var result = fields.reduce((o, f) => {
      let field = f.split('.')[0];
      let nested = (field == f) ? null : f.substring(f.indexOf('.') + 1);
      let existing = o[field] || {};
      let val = nested ? { ...existing, ...(Utility.projectObject0(obj[field], [nested])) } : obj[field]

      console.log({ obj, fields, field, nested, existing, val });

      return { ...o, [field]: val }
    }, {});
    return stringify ? JSON.stringify(result) : result;
  }

  static projectObject(obj: any, fields: string[], stringify = false): any {
    if (!obj) return null;

    let ans = {};

    for (let i = 0; i < fields.length; i++) {

      let isFieldNested = fields[i].indexOf('.') > -1;

      if (!isFieldNested) {
        ans[fields[i]] = obj[fields[i]];
        continue;
      }

      let fieldParts = fields[i].split('.');

      let mainObjectTracker = obj;
      let ansTracker = ans;

      for (let j = 0; j < fieldParts.length; j++) {
        if (j == fieldParts.length - 1) {
          ansTracker[fieldParts[j]] = mainObjectTracker[fieldParts[j]];
        }
        else if (mainObjectTracker[fieldParts[j]] === null) {
          ansTracker[fieldParts[j]] = null;
          break;
        }
        else if (mainObjectTracker[fieldParts[j]] === undefined) {
          ansTracker[fieldParts[j]] = undefined;
          break;
        }
        else {
          if (ansTracker[fieldParts[j]] === undefined) {
            ansTracker[fieldParts[j]] = {};
          }
          ansTracker = ansTracker[fieldParts[j]];
          mainObjectTracker = mainObjectTracker[fieldParts[j]];
        }
      }

    }

    return stringify ? JSON.stringify(ans) : ans;

  }

  static async getHttpRequest(url: string) {
    return new Promise<string>((resolve, reject) => {
      var x = new XMLHttpRequest();
      x.onreadystatechange = function () {
        if (x.readyState == 4) {
          if (x.status == 200)
            resolve(x.responseText);
          else reject(x.statusText);
        }
      }
      x.open('GET', url, true);
      x.send(null);
    });

  }

  static dateDiffMonths(from: Date, to: Date) {
    const _from = new PersianDate(from);
    const _to = new PersianDate(to);
    return (_to.year - _from.year) * 12 + (_to.month - _from.month) || 0;
  }

  static Array(length: number) { return length >= 0 ? new Array(length).fill(null) : null; }

  static getViewableChilds(element: HTMLElement, selector?: string) {
    if (!element) return [];
    var result: Element[] = [];
    const rect = element.getBoundingClientRect();
    const children = selector ? element.querySelectorAll(selector) : element.children;
    for (let i = 0; i < children.length; i++) {
      const child = children[i];
      if (!child) continue;
      const childRect = child.getBoundingClientRect();
      if (childRect.top >= rect.top && childRect.bottom <= rect.bottom &&
        childRect.left >= rect.left && childRect.right <= rect.right)
        result.push(child);
    }
    return result;
  }

  static blobToBase64(data: ArrayBuffer) {
    return new Promise<string>((resolve, reject) => {
      let reader = new FileReader();
      reader.readAsDataURL(new Blob([data]));
      reader.onloadend = () => resolve(reader.result as string);
      reader.onerror = error => reject(error);
    });
  }

  static isIranMobileNumber(number: string) {
    const mobileRegEx: RegExp = new RegExp(Patterns.mobile);
    return mobileRegEx.test(number);
  }
}
// Preserve original property order in kevalue pipe
export function originalOrder(a: any, b: any): number { return 0; }
export type dynamicFieldData = {
  name: string;
  title?: string;
  required?: boolean;
  config?: any;
};
export interface partialUpdate { Id: string }
export function getWeightedRandom(list: any[], weightProp: string, defaultWeight?: number) {
  if (!(list?.length > 0)) return null;
  const totalWeight = list.reduce((a, b) => a + b[weightProp] || defaultWeight, 0);
  let rand = Math.floor(Math.random() * totalWeight);
  let weightSum = 0;
  return list.find(v => {
    weightSum += v[weightProp] || defaultWeight || 1;
    return rand < weightSum;
  });
}

export function safeStringify(obj: any, replacer?: (key: string, value: any) => any, space = 2) {
  const ancestors: any[] = [];

  return JSON.stringify(
    obj,
    function (this: any, key, value) {
      const nextValue = replacer ? replacer.call(this, key, value) : value;

      if (typeof nextValue !== 'object' || nextValue === null) {
        return nextValue;
      }

      while (ancestors.length > 0 && ancestors[ancestors.length - 1] !== this) {
        ancestors.pop();
      }

      if (ancestors.includes(nextValue)) {
        return '[Circular]';
      }

      ancestors.push(nextValue);

      // Replace only true cyclic references while preserving shared siblings.
      return nextValue;
    },
    space
  );
}
